import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { array, number, object, string } from 'yup';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import { useSnackbar } from '@Providers/useSnackbar';
import { GridLayout } from '@Styled/utilities';
import {
	createEducationEntity,
	updateEducationEntity,
} from './education.requester';
import {
	EducationEntity,
	EducationSavePayload,
	EDUCATION_ENTITY_LABELS,
} from './education.types';

const DIALOG_SCHEMA = object({
	name: string().min(2).max(255).required(),
	language_ids: array().of(number().required()),
	year_ids: array().of(number().required()),
	section_ids: array().of(number().required()),
});

type FormValues = typeof DIALOG_SCHEMA.__outputType;

export type EducationDialogOption = { id: number; name: string };

export type EducationDialogInitial = {
	id: number;
	name: string;
	language_ids?: number[];
	year_ids?: number[];
	section_ids?: number[];
};

type Props = {
	open: boolean;
	entity: EducationEntity;
	initial?: EducationDialogInitial | null;
	languageOptions: EducationDialogOption[];
	yearOptions: EducationDialogOption[];
	sectionOptions: EducationDialogOption[];
	onClose: () => void;
	onSaved: () => void;
};

const EducationEntityDialog: React.FC<Props> = ({
	open,
	entity,
	initial,
	languageOptions,
	yearOptions,
	sectionOptions,
	onClose,
	onSaved,
}) => {
	const { displaySnackbar } = useSnackbar();
	const label = EDUCATION_ENTITY_LABELS[entity];

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { isSubmitting },
	} = useForm<FormValues>({
		resolver: yupResolver(DIALOG_SCHEMA),
		defaultValues: { name: '', language_ids: [], year_ids: [], section_ids: [] },
	});

	useEffect(() => {
		if (open) {
			reset({
				name: initial?.name ?? '',
				language_ids: initial?.language_ids ?? [],
				year_ids: initial?.year_ids ?? [],
				section_ids: initial?.section_ids ?? [],
			});
		}
	}, [open, initial]);

	const watchLanguages = watch('language_ids');
	const watchYears = watch('year_ids');
	const watchSections = watch('section_ids');

	const associations = useMemo(() => {
		if (entity === 'type')
			return [
				{
					name: 'language_ids' as const,
					label: 'Languages',
					options: languageOptions,
					selected: watchLanguages ?? [],
				},
				{
					name: 'year_ids' as const,
					label: 'Years',
					options: yearOptions,
					selected: watchYears ?? [],
				},
			];
		if (entity === 'year')
			return [
				{
					name: 'section_ids' as const,
					label: 'Sections',
					options: sectionOptions,
					selected: watchSections ?? [],
				},
			];
		return [];
	}, [
		entity,
		languageOptions,
		yearOptions,
		sectionOptions,
		watchLanguages,
		watchYears,
		watchSections,
	]);

	const onSubmit = async (values: FormValues) => {
		const payload: EducationSavePayload = { name: values.name };
		if (entity === 'type') {
			payload.language_ids = values.language_ids ?? [];
			payload.year_ids = values.year_ids ?? [];
		}
		if (entity === 'year') {
			payload.section_ids = values.section_ids ?? [];
		}
		try {
			if (initial) {
				await updateEducationEntity(entity, { ...payload, id: initial.id });
				displaySnackbar('success', `${label} "${values.name}" updated successfully`);
			} else {
				await createEducationEntity(entity, payload);
				displaySnackbar('success', `${label} "${values.name}" created successfully`);
			}
			onSaved();
		} catch (error: any) {
			const message =
				error?.response?.data?.errors?.[0]?.message ??
				error?.response?.data?.message ??
				`Unable to save ${label.toLowerCase()}`;
			displaySnackbar('error', message);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>
				{initial ? `Edit ${label}` : `Add ${label}`}
			</DialogTitle>
			<DialogContent>
				<FieldsLayout>
					<EdFormControl control={control} required name="name" label="Name" />
					{associations.map((association) => (
						<EdFormControl
							key={association.name}
							control={control}
							select
							multiple
							SelectProps={{
								multiple: true,
								renderValue: () =>
									association.selected
										.map(
											(id) =>
												association.options.find((_) => _.id === id)?.name
										)
										.filter(Boolean)
										.join(', '),
							}}
							name={association.name}
							label={association.label}
						>
							{association.options.map((option) => (
								<MenuItem key={option.id} value={option.id}>
									<Checkbox
										checked={association.selected.includes(option.id)}
									/>
									{option.name}
								</MenuItem>
							))}
						</EdFormControl>
					))}
				</FieldsLayout>
			</DialogContent>
			<DialogActions>
				<Button color="warning" onClick={onClose}>
					Cancel
				</Button>
				<EdButton
					edcolor="purple"
					disabled={isSubmitting}
					onClick={handleSubmit(onSubmit)}
				>
					Save
				</EdButton>
			</DialogActions>
		</Dialog>
	);
};

export default EducationEntityDialog;

const FieldsLayout = styled(GridLayout)`
	grid-template-columns: 1fr;
	gap: 1.5rem;
	padding-top: 0.5rem;
`;
